import { useMemo, useRef } from "react";
import { useUploadManager } from "@/context/UploadManagerContext";
import { toast } from "sonner";
import {
  ChevronDown, ChevronUp, X, CheckCircle2, AlertTriangle, Loader2, RotateCw,
  UploadCloud, PlayCircle,
} from "lucide-react";

const fmtSize = (b) => {
  if (b == null) return "";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

function StatusIcon({ status }) {
  if (status === "done") return <CheckCircle2 data-testid="upload-done-icon" className="h-4 w-4 text-emerald-400" />;
  if (status === "error") return <AlertTriangle className="h-4 w-4 text-red-400" />;
  if (status === "interrupted") return <PlayCircle data-testid="upload-interrupted-icon" className="h-4 w-4 text-[#FFDD03]" />;
  if (status === "retrying") return <RotateCw className="h-4 w-4 text-[#FFDD03] animate-spin" />;
  if (status === "processing") return <Loader2 className="h-4 w-4 text-[#00F0FF] animate-spin" />;
  if (status === "pending") return <Loader2 className="h-4 w-4 text-white/40 animate-spin" />;
  return <Loader2 className="h-4 w-4 text-[#00F0FF] animate-spin" />;
}

export function UploadManagerWidget() {
  const mgr = useUploadManager();
  const { uploads, minimized, setMinimized, cancel, retry, resume, dismiss, closeManager } = mgr;
  const fileInputRef = useRef(null);
  const resumeTargetRef = useRef(null);

  const summary = useMemo(() => {
    const active = uploads.filter((u) => ["uploading", "pending", "retrying", "processing"].includes(u.status)).length;
    const done = uploads.filter((u) => u.status === "done").length;
    const failed = uploads.filter((u) => u.status === "error").length;
    const interrupted = uploads.filter((u) => u.status === "interrupted").length;
    return { active, done, failed, interrupted };
  }, [uploads]);

  if (uploads.length === 0) return null;

  const onResumeClick = (id) => {
    resumeTargetRef.current = id;
    fileInputRef.current?.click();
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    const id = resumeTargetRef.current;
    e.target.value = "";
    if (!file || !id) return;
    const res = resume(id, file);
    if (!res.ok) toast.error(res.error || "Couldn't resume upload");
    else toast.success("Resuming upload…");
  };

  const title = summary.active > 0
    ? `Uploading ${summary.active} item${summary.active > 1 ? "s" : ""}`
    : summary.interrupted > 0
      ? `${summary.interrupted} upload${summary.interrupted > 1 ? "s" : ""} paused`
      : summary.failed > 0
        ? `${summary.failed} upload${summary.failed > 1 ? "s" : ""} failed`
        : `${summary.done} upload${summary.done > 1 ? "s" : ""} complete`;

  return (
    <div
      data-testid="upload-manager"
      className="fixed bottom-5 right-5 z-[60] w-[360px] max-w-[calc(100vw-2.5rem)] rounded-2xl border border-white/10 bg-[#0d0d0d] shadow-2xl shadow-black/60 overflow-hidden"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        data-testid="resume-file-input"
        onChange={onFilePicked}
        className="hidden"
      />
      <div className="flex items-center justify-between px-4 py-3 bg-white/[0.04] border-b border-white/10">
        <span className="flex items-center gap-2 text-sm font-semibold text-white">
          <UploadCloud className="h-4 w-4 text-[#00F0FF]" />
          <span data-testid="upload-manager-title">{title}</span>
        </span>
        <div className="flex items-center gap-1">
          <button
            data-testid="upload-manager-toggle"
            onClick={() => setMinimized(!minimized)}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 transition-colors"
            aria-label={minimized ? "Expand" : "Collapse"}
          >
            {minimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            data-testid="upload-manager-close"
            onClick={closeManager}
            disabled={summary.active > 0}
            title={summary.active > 0 ? "Uploads in progress" : "Close"}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 transition-colors disabled:opacity-30"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <div className="max-h-[45vh] overflow-y-auto divide-y divide-white/5">
          {summary.interrupted > 0 && (
            <div className="px-4 py-2 text-xs text-[#FFDD03]/80 bg-[#FFDD03]/5">
              Some uploads were interrupted by a page reload. Click <b>Resume</b> and re-select the same file — it continues where it left off.
            </div>
          )}
          {uploads.map((u) => (
            <div key={u.id} data-testid={`upload-item-${u.id}`} className="px-4 py-3">
              <div className="flex items-center gap-3">
                <StatusIcon status={u.status} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate" title={u.name}>{u.name}</p>
                  <p className="text-xs text-white/40">
                    {u.status === "done" ? "Completed"
                      : u.status === "error" ? (u.error || "Failed")
                      : u.status === "processing" ? "Processing… almost done"
                      : u.status === "interrupted" ? `Paused at ${u.progress || 0}% — resume to continue`
                      : u.status === "retrying" ? "Connection lost — resuming…"
                      : u.status === "pending" ? "Queued"
                      : `${u.progress}% · ${fmtSize(u.size)}`}
                  </p>
                </div>
                {u.status === "interrupted" ? (
                  <button data-testid={`upload-resume-${u.id}`} onClick={() => onResumeClick(u.id)}
                    className="text-xs font-semibold text-[#FFDD03] hover:underline">Resume</button>
                ) : u.status === "error" ? (
                  <button data-testid={`upload-retry-${u.id}`} onClick={() => retry(u.id)}
                    className="text-xs text-[#00F0FF] hover:underline">Retry</button>
                ) : u.status === "done" ? (
                  <button data-testid={`upload-dismiss-${u.id}`} onClick={() => dismiss(u.id)}
                    className="p-1 rounded hover:bg-white/10 text-white/40"><X className="h-3.5 w-3.5" /></button>
                ) : u.status === "processing" ? null : (
                  <button data-testid={`upload-cancel-${u.id}`} onClick={() => cancel(u.id)}
                    className="text-xs text-white/50 hover:text-red-400 transition-colors">Cancel</button>
                )}
              </div>
              {["uploading", "retrying", "pending", "interrupted", "processing"].includes(u.status) && (
                <div className="mt-2 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                  <div
                    data-testid={`upload-progress-bar-${u.id}`}
                    className={`h-full rounded-full transition-all duration-300 ${
                      u.status === "retrying" ? "bg-[#FFDD03]"
                        : u.status === "interrupted" ? "bg-[#FFDD03]/50"
                        : u.status === "processing" ? "bg-[#00F0FF] animate-pulse"
                        : "bg-[#00F0FF]"}`}
                    style={{ width: `${u.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
